/**
 * src/utils/nativeColor.ts — hand a NATIVE plugin a colour it can actually parse.
 *
 * Capacitor plugins parse colour arguments themselves: on Android
 * `StatusBar.setBackgroundColor` and `GoogleMap.addPolylines` both end at
 * `Color.parseColor`, which understands `#RRGGBB` and nothing else. A CSS
 * `var(--token)` is meaningless there — the call rejects, and because both call
 * sites sit inside best-effort try/catch blocks the failure is silent.
 *
 * That is not hypothetical. `scripts/tokenise-brand-hexes.mjs` swept literal
 * brand hexes into `var()` across the app, correctly, because a literal hex in
 * an inline style cannot follow a mode switch. Its skip list covered MapLibre
 * paint expressions but not these two, so the driver route line stopped drawing
 * and the Android status bar lost its tint, both without an error anyone saw.
 *
 * The fix is not to go back to literals, which drift from the palette the next
 * time it is retuned. It is to keep the token as the source of truth and RESOLVE
 * it before it crosses into native. The fallback covers the only cases where
 * that can fail: a call made before the stylesheet applies, or a renamed token.
 */

/** Resolve a CSS custom property on :root to its computed value. */
export function resolveTokenColor(token: string, fallback: string): string {
  if (typeof document === 'undefined') return fallback;
  try {
    const value = getComputedStyle(document.documentElement)
      .getPropertyValue(token)
      .trim();
    // A var() that resolves to another var() is still not a colour; only take
    // a value that looks like one.
    return /^#|^rgb|^hsl/i.test(value) ? value : fallback;
  } catch {
    return fallback;
  }
}

/**
 * Is this colour dark enough that foreground icons should be light?
 *
 * Only needed for the status bar, where the tint and the icon style are set by
 * two different calls and have to agree. Getting it wrong is the failure the
 * comment in main.tsx describes: near-invisible icons.
 */
export function isDarkColor(color: string, fallback = false): boolean {
  const hex = color.trim().replace(/^#/, '');
  const full = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex;
  if (!/^[0-9a-f]{6}$/i.test(full)) return fallback;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  // Perceived luminance, sRGB weights. 0.5 is the conventional split.
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255 < 0.5;
}
